import { Toggle } from '@/components/inputs';
import { Loadable } from '@/components/loaders';
import { SupportModal } from '@/features/layout/Sidebars/common/components/SidebarBase/SidebarNavigation/Items/SidebarSupportItem/SupportModal';
import RouteConstants from '@/features/router/RouteConstants';
import { SupportSuccessModal } from '@/features/support';
import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useModalContext } from '@/hooks/useModalContext';
import { DistributionChannelConnector } from '@/models/distributionChannels';
import { ToastType } from '@/models/toast';
import { useAuthenticateConnectorMutation, useLazyGetDistributionChannelConnectorsQuery } from '@/services/endpoints/distributionChannels';
import { popupCenter } from '@/utils';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { ConnectorInReviewModal } from '../ConnectorInReviewModal';
import { DeauthenticateConnectorModal } from '../DeauthenticateConnectorModal';

type CyclrDestinationConnectionToggleProps = {
  connector: DistributionChannelConnector;
  deauthenticateDisabled?: boolean;
  onChangeConnectionInProgress?: (connector: DistributionChannelConnector | null) => void;
};

export const CyclrDestinationConnectionToggle: FC<CyclrDestinationConnectionToggleProps> = ({
  connector,
  deauthenticateDisabled = true,
  onChangeConnectionInProgress,
}) => {
  const dispatch = useDispatch();
  const { id: workspaceId } = useWorkspace();
  const { openModal } = useModalContext();
  const { t } = useTranslation();

  const { name, installed, authenticated, integrationPartner } = connector;
  const [authenticateConnector, { isSuccess, data: authResponse }] = useAuthenticateConnectorMutation();
  const [getConnectors] = useLazyGetDistributionChannelConnectorsQuery();

  const isConfigured = installed && authenticated;
  const [connectionInProgress, setConnectionInProgress] = useState(false);

  useEffect(() => {
    if (isSuccess && authResponse) {
      console.log(authResponse);
      const connectorId = authResponse.connector.accountConnectorId;
      if (connectorId) {
        const url = `${authResponse.authUrl}?id=${connectorId}&token=${authResponse.token}&targetOrigin=${location.origin + RouteConstants.cyclrAuthRedirect}`;
        (window as any).cyclrDeauthenticationCompleted = (success: boolean, w: Window) => {
          w.close.apply(w);
          delete (window as any).cyclrDeauthenticationCompleted;
          getConnectors({ workspaceId });
          dispatch(
            showToast({
              type: ToastType.SUCCESS,
              title: t('ALL:destinationConnected'),
              message: t('ALL:destinationWasSuccessfullyConnected', { name: connector.name }),
            })
          );
        };
        (window as any).authWindow = popupCenter({
          url,
          title: t('ALL:authenticate'),
          w: 640,
          h: 640,
        });

        const checkWindowClosed = setInterval(() => {
          if (!(window as any).authWindow || (window as any).authWindow.closed) {
            clearInterval(checkWindowClosed);

            connectorConnectionInProgress(false);
          }
        }, 500);
      } else {
        dispatch(
          showToast({
            type: ToastType.ERROR,
            title: t('ALL:couldNotConnectDestination', { name: connector.name }),
          })
        );
      }
    }
  }, [isSuccess]);

  const openDeauthenticateConnectorModal = () => {
    openModal({
      renderContent: DeauthenticateConnectorModal,
      renderContentProps: {
        workspaceId,
        connector,
      },
      dismissable: true,
      fullWidth: false,
      fullHeight: false,
      cancelLabel: 'cancell',
    });
  };

  const [showedModal, setShowedModal] = useState<'default' | 'connectorInReview' | 'supportSuccess' | null>(null);

  const closeModal = () => setShowedModal(null);
  const showSupportModal = () => setShowedModal('default');
  const showConnectorInReviewModal = () => setShowedModal('connectorInReview');
  const showSupportSuccessModal = () => setShowedModal('supportSuccess');

  const isSupportModalVisible = showedModal === 'default';
  const isConnectorInReviewModalVisible = showedModal === 'connectorInReview';
  const isSupportSuccessModalVisible = showedModal === 'supportSuccess';

  const handleConnectorConnection = ($event) => {
    const { checked } = $event.target;
    if (checked && !isConfigured) {
      if (name === 'Salesforce' || name === 'Salesforce Marketing Cloud') {
        showConnectorInReviewModal();
        return;
      }

      connectorConnectionInProgress(checked);
      authenticateConnector({ workspaceId, connectorName: name, integrationPartner });

      onChangeConnectionInProgress?.(connector);
    } else if (!checked && isConfigured) {
      openDeauthenticateConnectorModal();
    }
  };

  const connectorConnectionInProgress = (status: boolean) => {
    setConnectionInProgress(status);

    onChangeConnectionInProgress?.(null);
  };

  return (
    <Loadable className='h-5' isLoading={connectionInProgress}>
      <Toggle
        label={t(isConfigured ? 'ALL:destinationConnected' : 'ALL:destinationDisconnected')}
        labelPosition='end'
        labelWrapperClassName='capitalize text-sm text-black-100'
        checked={isConfigured}
        onChange={handleConnectorConnection}
        disabled={deauthenticateDisabled}
      />
      {isConnectorInReviewModalVisible && <ConnectorInReviewModal onConfirm={async () => showSupportModal()} onClose={closeModal} />}
      {isSupportModalVisible && <SupportModal onConfirm={showSupportSuccessModal} onClose={closeModal} />}
      {isSupportSuccessModalVisible && <SupportSuccessModal onConfirm={async () => closeModal()} onClose={closeModal} />}
    </Loadable>
  );
};
